import { Camera, Loader2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { initials } from "../../lib/format";

/**
 * Resize an image File to a square JPEG dataURI ~320×320 (cover crop).
 * Returns a string `data:image/jpeg;base64,...` ready to be stored as avatar_url.
 */
async function fileToSquareJpegDataURI(file, size = 320, quality = 0.82) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Lecture du fichier impossible"));
        reader.onload = () => {
            const img = new Image();
            img.onerror = () => reject(new Error("Image invalide"));
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext("2d");
                // Cover crop: scale to fit smallest dimension, center.
                const ratio = Math.max(size / img.width, size / img.height);
                const dw = img.width * ratio;
                const dh = img.height * ratio;
                ctx.fillStyle = "#FAF7F2";
                ctx.fillRect(0, 0, size, size);
                ctx.drawImage(img, (size - dw) / 2, (size - dh) / 2, dw, dh);
                resolve(canvas.toDataURL("image/jpeg", quality));
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    });
}

/**
 * Avatar uploader.
 *
 * Props:
 *  - value: current avatar URL or data URI (string|null)
 *  - name: user name (for initials fallback)
 *  - onChange(dataUri): called when a new image is selected (already resized to ~30KB)
 *  - size: pixel size of the preview (default 96)
 *  - testId: data-testid prefix
 */
export default function AvatarUploader({ value, name, onChange, size = 96, testId = "avatar" }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);

    async function handleFile(file) {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Le fichier doit être une image (JPEG, PNG, WEBP...)");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Image trop lourde (max 8 Mo avant redimensionnement)");
            return;
        }
        setBusy(true);
        try {
            const dataUri = await fileToSquareJpegDataURI(file);
            onChange?.(dataUri);
        } catch (e) {
            toast.error(e.message || "Erreur lors du traitement de l'image");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="flex items-center gap-4">
            <div
                className="relative rounded-2xl overflow-hidden bg-shariti-orange-soft shrink-0"
                style={{ width: size, height: size }}
            >
                {value ? (
                    <img src={value} alt="" className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-shariti-orange font-extrabold" style={{ fontSize: size / 3.5 }}>
                        {initials(name)}
                    </div>
                )}
                {busy && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-white" />
                    </div>
                )}
            </div>
            <div className="space-y-1.5">
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    className="hidden"
                    data-testid={`${testId}-input`}
                />
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    data-testid={`${testId}-pick`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-shariti-orange text-white hover:bg-[#d66528] px-4 h-9 text-[11px] font-extrabold transition shadow-btn"
                >
                    <Upload className="h-3.5 w-3.5" />
                    {value ? "Changer la photo" : "Choisir une photo"}
                </button>
                {value && (
                    <button
                        type="button"
                        onClick={() => onChange?.(null)}
                        data-testid={`${testId}-remove`}
                        className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-shariti-red transition"
                    >
                        Retirer la photo
                    </button>
                )}
                <div className="text-[10px] font-medium text-slate-400 max-w-[200px]">
                    <Camera className="inline h-3 w-3 mr-0.5" /> JPEG / PNG / WEBP — auto-redimensionné en 320×320.
                </div>
            </div>
        </div>
    );
}
